// GENERATED BY portal-gen — DO NOT EDIT.
//
// Source: Billing — billing.v1.yaml
// Regenerate with `npm run generate`; `npm run check` byte-diffs this file against a
// fresh run and fails the build on any difference.
//
// Every object below preserves the contract's `additionalProperties: false` as `.strict()`.
// That is a privacy control, not a typing convenience: a control-plane response that ever
// grows a content-bearing field must fail to parse, not render.

import { z } from "zod";

/** A purchasable add-on beyond the plan's base bundle. Prompt Guard is the ONLY add-on and is sales-led on every plan — it is never self-serve. (2026-09-02: `plan` was removed as an add-on because Prompt Plan is now part of the base bundle on all three plans.) */
export const AddOn = z.literal("guard");
export type AddOn = z.infer<typeof AddOn>;

export const Plan = z.enum(["individual", "team", "enterprise"]);
export type Plan = z.infer<typeof Plan>;

export const Interval = z.enum(["month", "year"]);
export type Interval = z.infer<typeof Interval>;

export const CheckoutSessionRequest = z.object({
  plan: Plan,
  interval: Interval,
  seats: z.number().int().optional(),
  addons: z.array(AddOn).optional(),
}).strict();
export type CheckoutSessionRequest = z.infer<typeof CheckoutSessionRequest>;

export const CheckoutSessionResponse = z.object({
  sessionId: z.string(),
  url: z.string().url(),
}).strict();
export type CheckoutSessionResponse = z.infer<typeof CheckoutSessionResponse>;

/** A reference to a Stripe invoice. NO line items or invoice bodies are stored/returned. */
export const InvoiceRef = z.object({
  id: z.string(),
  number: z.string().nullable().optional(),
  status: z.enum(["draft", "open", "paid", "uncollectible", "void"]),
  amountDue: z.number().int(),
  amountPaid: z.number().int().optional(),
  currency: z.string(),
  created: z.string().datetime(),
  hostedInvoiceUrl: z.string().url().nullable().optional(),
  pdfUrl: z.string().url().nullable().optional(),
}).strict();
export type InvoiceRef = z.infer<typeof InvoiceRef>;

export const InvoiceList = z.object({
  data: z.array(InvoiceRef),
  hasMore: z.boolean(),
  nextCursor: z.string().nullable().optional(),
}).strict();
export type InvoiceList = z.infer<typeof InvoiceList>;

export const PortalLinkResponse = z.object({
  url: z.string().url(),
}).strict();
export type PortalLinkResponse = z.infer<typeof PortalLinkResponse>;

/** A product the org is entitled to under its plan + add-ons. */
export const Product = z.enum(["gateway", "smart", "guard", "plan"]);
export type Product = z.infer<typeof Product>;

/** trialing = local no-card trial (no Stripe sub yet); active = paid; past_due = a payment failed and dunning is in progress; canceled = ended; locked = trial expired without conversion (downgraded). */
export const SubscriptionStatus = z.enum(["trialing", "active", "past_due", "canceled", "locked", "incomplete"]);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatus>;

export const Subscription = z.object({
  orgId: z.string().uuid(),
  plan: Plan,
  interval: z.enum(["month", "year"]).nullable().optional(),
  status: SubscriptionStatus,
  seatLimit: z.number().int(),
  seatCount: z.number().int(),
  trialEndsAt: z.string().datetime().nullable().optional(),
  currentPeriodEnd: z.string().datetime().nullable().optional(),
  cancelAtPeriodEnd: z.boolean().optional(),  // default: false
  products: z.array(Product),
  addons: z.array(AddOn).optional(),
}).strict();
export type Subscription = z.infer<typeof Subscription>;

// ---- Operations ----
//
// `roleGate` is documentation carried into the type system, NOT enforcement. It lets a
// lint test assert that every admin-gated call site sits inside a RequireAdmin subtree,
// and it lets the client attach the right context to a 403.

/** Create a Stripe Checkout Session for the caller's org (admin only). */
export const createCheckoutSession = {
  id: "createCheckoutSession",
  method: "POST",
  path: "/v1/billing/checkout-session",
  roleGate: "admin",
  pathParams: [] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: CheckoutSessionRequest,
  successStatus: 201,
  response: CheckoutSessionResponse,
  errors: [400, 401, 403, 409, 503] as const,
} as const;
export type CreateCheckoutSessionRequest = z.infer<typeof createCheckoutSession.request>;
export type CreateCheckoutSessionResponse = z.infer<typeof createCheckoutSession.response>;

/** Create a Stripe Customer Portal session for the caller's org (admin only). */
export const createPortalLink = {
  id: "createPortalLink",
  method: "POST",
  path: "/v1/billing/portal-link",
  roleGate: "admin",
  pathParams: [] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: null,
  successStatus: 201,
  response: PortalLinkResponse,
  errors: [401, 403, 409, 503] as const,
} as const;
export type CreatePortalLinkResponse = z.infer<typeof createPortalLink.response>;

/** Get the caller's org subscription + entitlement state (admin only). */
export const getSubscription = {
  id: "getSubscription",
  method: "GET",
  path: "/v1/billing/subscription",
  roleGate: "admin",
  pathParams: [] as const,
  queryParams: {} as const,
  requiresIfMatch: false,
  request: null,
  successStatus: 200,
  response: Subscription,
  errors: [401, 403, 404] as const,
} as const;
export type GetSubscriptionResponse = z.infer<typeof getSubscription.response>;

/** List the org's Stripe invoices as references (admin only). */
export const listInvoices = {
  id: "listInvoices",
  method: "GET",
  path: "/v1/billing/invoices",
  roleGate: "admin",
  pathParams: [] as const,
  queryParams: {
    startingAfter: { required: false },
    limit: { required: false },
  } as const,
  requiresIfMatch: false,
  request: null,
  successStatus: 200,
  response: InvoiceList,
  errors: [401, 403, 409, 503] as const,
} as const;
export type ListInvoicesResponse = z.infer<typeof listInvoices.response>;

export const billingOperations = { createCheckoutSession, createPortalLink, getSubscription, listInvoices } as const;
